import { AnyAction } from 'redux';
import { SETTINGS_ACTIONS } from '../actions/index';
import { SettingsState } from '../types/state';
import produce from 'immer';
import { ALL } from '../constants';

const initialState: SettingsState = {
  isLoading: false,
  data: undefined,
  properties: undefined,
  selectedProperty: { id: 'default', name: ALL },
  selectedStrategyProperty: undefined,

  users: undefined,
  markets: undefined,
  schedulerReports: undefined,
  rulesEngineData: undefined,
  /*custom strat*/
  fetchedStrategy: { id: null, data: null },
  customStrategyParameters: undefined,
  nStrategy: undefined,
  nParameters: undefined,
  //new custom
  strategiesState: undefined,
  strategies: undefined,
  offerGenerationStatus: undefined,
  marketRent: undefined,
  renewalOffersTaskExecutionId: undefined,
  newLeaseOffersTaskExecutionId: undefined,
  renewalOfferGenerationStatus: undefined,
  newLeaseOfferGenerationStatus: undefined,
  offerGenerationLastExecution: undefined,
};

const settingsReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case SETTINGS_ACTIONS.LOADING: {
      return produce(state, (dft) => {
        dft.isLoading = action.payload;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.FETCH_SCHEDULED_REPORTS: {
      return produce(state, (dft) => {
        dft.schedulerReports = action.payload.data;
        dft.isLoading = false;
        return dft;
      });
    }
    case SETTINGS_ACTIONS.UPDATE_SCHEDULED_REPORT: {
      return produce(state, (dft) => {
        dft.schedulerReports = action.payload.data;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.SET_OFFER_GENERATION_STATUS: {
      return produce(state, (dft) => {
        dft.offerGenerationStatus = action.payload;
        return dft;
      });
    }
    case SETTINGS_ACTIONS.CLEAR_OFFER_GENERATION_STATUS: {
      return produce(state, (dft) => {
        dft.offerGenerationStatus = action.payload;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.FETCH_DATA: {
      return produce(state, (dft) => {
        dft.data = action.payload.masterSettingsData;
        dft.selectedProperty = action.payload.selectedProperty;
        dft.properties = action.payload.propertiesData;
        dft.markets = action.payload.markets;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.FETCH_RULES_ENGINE_DATA: {
      return produce(state, (dft) => {
        dft.rulesEngineData = action.payload.rulesEngineData;
        dft.selectedProperty = action.payload.selectedProperty;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.FETCH_STRATEGIES: {
      return produce(state, (dft) => {
        if (!dft.strategies) dft.strategies = action.payload.strategies;
        if (!dft.strategiesState)
          dft.strategiesState = action.payload.strategiesState;

        if (!dft.properties) dft.properties = action.payload.properties;
        if (!dft.markets) dft.markets = action.payload.markets;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.UPDATE_USER: {
      return produce(state, (dft) => {
        dft.users = action.payload;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.UPDATE_SETTINGS: {
      return produce(state, (dft) => {
        dft.data = action.payload;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.UPDATE_RULES_ENGINE: {
      return produce(state, (dft) => {
        dft.rulesEngineData = action.payload;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.UPDATE_SETTINGS_FAILED: {
      return produce(state, (dft) => {
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.UPDATE_USER_FAILED: {
      return produce(state, (dft) => {
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.REMOVE_NOTIFICATION: {
      return produce(state, (dft) => {
        dft.notifications = action.payload;
      });
    }

    case SETTINGS_ACTIONS.FETCH_USERS: {
      return produce(state, (dft) => {
        dft.users = action.payload;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.CREATE: {
      return produce(state, (dft) => {
        dft.strategies.cards.map((card: any) => {
          let key: string = 'newLease';
          if (action.payload.StrategyType === 'Renewal.Strategy') {
            key = 'renewals';
          }

          if (action.payload.PropertyCode === card.code) {
            card[key].activeStrategy = action.payload.Value;
            card[key].updateId = action.payload.id;
          }

          return card;
        });

        return dft;
      });
    }
    case SETTINGS_ACTIONS.RESET: {
      return produce(state, (dft) => {
        dft.strategies = undefined;
        dft.strategiesState = undefined;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.SET_PROPERTY: {
      return produce(state, (dft) => {
        dft.selectedProperty = action.payload.selectedProperty;
        dft.data = action.payload.masterSettingsData;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.SET_STRATEGY_PROPERTY: {
      return produce(state, (dft) => {
        dft.selectedStrategyProperty = action.payload.selectedStrategyProperty;
        dft.offerGenerationStatus = undefined;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.GET_CUSTOM_STRATEGY_PARAMETERS: {
      return produce(state, (dft) => {
        dft.customStrategyParameters = action.payload;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.FETCH_MARKET_RENT_DATA: {
      return produce(state, (dft) => {
        dft.marketRent = action.payload;
        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.UPDATE_MARKET_RENT_DATA: {
      return produce(state, (dft) => {
        const marketRents = action.payload;

        marketRents.forEach((updatedRent) => {
          const index = state.marketRent?.findIndex(
            (rent) =>
              rent.Property_Code === updatedRent.Property_Code &&
              rent.Unit_Type_Code === updatedRent.Unit_Type_Code
          );

          if (index !== -1) dft.marketRent[index] = updatedRent;
        });

        dft.isLoading = false;
        return dft;
      });
    }

    case SETTINGS_ACTIONS.GENERATE_RENEWAL_OFFERS: {
      return produce(state, (dft) => {
        dft.renewalOffersTaskExecutionId = action.payload.taskExecutionId;
        dft.isLoading = false;

        return dft;
      });
    }

    case SETTINGS_ACTIONS.GENERATE_NEW_LEASE_OFFERS: {
      return produce(state, (dft) => {
        dft.newLeaseOffersTaskExecutionId = action.payload.taskExecutionId;
        dft.isLoading = false;

        return dft;
      });
    }
    case SETTINGS_ACTIONS.SET_MARKET_RENT_OFFER_GENERATION_STATUS: {
      return produce(state, (dft) => {
        const { newLeaseOfferGenerationStatus, renewalOfferGenerationStatus } =
          action.payload.data;

        dft.renewalOffersTaskExecutionId =
          newLeaseOfferGenerationStatus?.ExecutionId;
        dft.newLeaseOffersTaskExecutionId =
          renewalOfferGenerationStatus?.ExecutionId;

        dft.newLeaseOfferGenerationStatus =
          newLeaseOfferGenerationStatus?.Status;
        dft.renewalOfferGenerationStatus = renewalOfferGenerationStatus?.Status;

        dft.offerGenerationLastExecution =
          newLeaseOfferGenerationStatus?.createdAt;

        return dft;
      });
    }
    case SETTINGS_ACTIONS.CLEAR_MARKET_RENT_OFFER_GENERATION_STATUS: {
      return produce(state, (dft) => {
        dft.renewalOfferGenerationStatus = action.payload;
        dft.newLeaseOfferGenerationStatus = action.payload;

        return dft;
      });
    }

    case 'INITIALIZE': {
      return state;
    }
    case 'LOGOUT': {
      return initialState;
    }
    default: {
      return state;
    }
  }
};

export default settingsReducer;
